type Tone = "neutral" | "amber" | "red" | "positive";

const TONE: Record<Tone, { bg: string; border: string; label: string }> = {
  neutral: {
    bg: "bg-neutral-900",
    border: "border-neutral-800",
    label: "text-neutral-400",
  },
  amber: {
    bg: "bg-amber-500/5",
    border: "border-amber-500/30",
    label: "text-amber-300",
  },
  red: {
    bg: "bg-red-500/5",
    border: "border-red-500/30",
    label: "text-red-300",
  },
  positive: {
    bg: "bg-lime/5",
    border: "border-lime/30",
    label: "text-lime",
  },
};

/**
 * Card de KPI do dashboard fiscal.
 *
 *   - `value`: string já formatada (FR).
 *   - `secondaryLabel/value`: linha secundária (ex: "Anualizado: 12.345 €").
 *   - `delta`: variação opcional (ex: "+5,21%").
 *   - `tone`: cor do card. `red` = crítico, `amber` = aviso, `positive` = lime/sucesso.
 *   - `subtitle`: explica o que é o KPI ou contexto temporal.
 *   - `tooltip`: hint em hover (fórmula, fonte).
 */
export function KpiCard({
  label,
  value,
  secondaryLabel,
  secondaryValue,
  delta,
  deltaColor,
  tone = "neutral",
  subtitle,
  tooltip,
  loading,
}: {
  label: string;
  value: string;
  secondaryLabel?: string;
  secondaryValue?: string;
  delta?: string;
  deltaColor?: "positive" | "red" | "neutral";
  tone?: Tone;
  subtitle?: string;
  tooltip?: string;
  loading?: boolean;
}) {
  const t = TONE[tone];
  const deltaCls =
    deltaColor === "positive"
      ? "text-lime"
      : deltaColor === "red"
        ? "text-red-400"
        : "text-neutral-500";

  return (
    <div
      className={`rounded-lg border ${t.border} ${t.bg} p-4 flex flex-col gap-1`}
      title={tooltip}
    >
      <div className="flex items-start justify-between">
        <span className={`text-xs uppercase tracking-wider ${t.label} leading-tight`}>{label}</span>
        {delta && <span className={`text-xs font-mono ${deltaCls}`}>{delta}</span>}
      </div>
      {loading ? (
        <div className="h-7 w-24 rounded bg-neutral-800/60 animate-pulse my-1" />
      ) : (
        <div className="text-2xl font-semibold font-mono tabular-nums leading-tight mt-1">
          {value}
        </div>
      )}
      {(secondaryLabel || secondaryValue) && (
        <div className="text-xs text-neutral-500 flex justify-between gap-3 mt-1">
          {secondaryLabel && <span>{secondaryLabel}</span>}
          {secondaryValue && <span className="font-mono tabular-nums">{secondaryValue}</span>}
        </div>
      )}
      {subtitle && <div className="text-[10px] text-neutral-600 mt-1">{subtitle}</div>}
    </div>
  );
}
